import clsx from 'clsx';
import { useEffect, useMemo, useRef, useState } from 'react';
import useTestTag from '../../common/hooks/use-test-tag';
import type { OdinKey } from '../../common/types/odin-key.type';
import HelpComponent from '../help-component/help.component';
import Tooltip from '../tooltip-component/tooltip.component';
import { DataProxyInstanceService } from './data-proxy-instance.service';
import { DynamicFieldService } from './dynamic-field.service';
import { DynamicFormRequiredType } from './dynamic-form.enums';
import type {
  DynamicFieldDataProps,
  DynamicFormFieldPermissions,
  DynamicFormFieldType,
  FieldValueConverter,
  HfDynamicFieldComponentProps,
} from './dynamic-form.interfaces';
import { applyExtraProperties } from './dynamic-form-common';

export interface DynamicFieldProps {
  id: OdinKey;
  dynamicFormId: string;
  value?: unknown;
  fieldName: string;
  fieldType: DynamicFormFieldType;
  caption: string;
  required?: DynamicFormRequiredType;
  error?: string;
  dynamicProperties?: Record<string, unknown>;
  readOnly?: boolean;
  customFieldType?: string;
  helpMessage?: string;
  hintMessage?: string;
  fieldValueConverter?: FieldValueConverter;
  permissions?: DynamicFormFieldPermissions;
  hidden?: boolean;
  scrollId?: string;
  labelStyles?: string;
  resetField: (value?: unknown) => void;
  setFieldValue: (value?: unknown) => void;
}

const DynamicField = ({
  id,
  dynamicFormId,
  value,
  fieldName,
  fieldType,
  caption,
  required = DynamicFormRequiredType.False,
  error,
  dynamicProperties,
  readOnly,
  customFieldType,
  helpMessage,
  hintMessage,
  fieldValueConverter,
  permissions,
  hidden,
  scrollId,
  labelStyles,
  resetField,
  setFieldValue,
}: DynamicFieldProps) => {
  const hintMessageAsPlaceholder = useRef([
    'TEXTFIELD',
    'TEXTAREA',
    'COMBOBOX',
    'RECORD_COMBOBOX',
    'INTFIELD',
    'DOUBLEFIELD',
    'MULTISELECTFIELD',
  ]);
  const fieldService = useRef(DynamicFieldService.getServiceInstance());
  const dataProxyService = useRef(DataProxyInstanceService.getServiceInstance(dynamicFormId));
  const Component = useMemo(
    () => fieldService.current.getFieldComponent(fieldType, customFieldType),
    [fieldType, customFieldType]
  );

  const [isHidden, setIsHidden] = useState(hidden);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Only trigger on hidden prop change, not on internal state or callbacks
  useEffect(() => {
    if (isHidden !== hidden) {
      if (hidden === true) {
        // reset field if it becomes hidden to prevent it being marked as dirty or sent with the record data
        resetField(value);
      } else if (hidden === false) {
        // mark it as dirty if it becomes visible so it's sent with the record data
        resetField(null); // we have to reset it to null first,
        setFieldValue(value); // then set the value — shouldDirty is controlled by the parent
      }

      setIsHidden(hidden);
    }
  }, [hidden]);

  let dynamicProps: Partial<HfDynamicFieldComponentProps<unknown, unknown> & DynamicFieldDataProps> = {
    ...dynamicProperties,
    value,
    label: caption,
    labelStyles: labelStyles,
    required,
    error,
    readOnly,
    permissions,
    id,
    name: fieldName,
    placeholder: hintMessageAsPlaceholder.current.includes(fieldType) ? hintMessage : undefined,
    fieldValueConverter,
  };

  dynamicProps = applyExtraProperties(dynamicProps);

  // TODO: Determine if there is a way for the user to pass the checkboxes value and if so remove this code
  if (fieldType === 'CHECKBOX') {
    // checking a checkbox should return the value of true
    dynamicProps['value'] = true;
  } else if (['TEXTFIELD', 'INTFIELD', 'DOUBLEFIELD'].includes(fieldType)) {
    dynamicProps['enableDebounce'] = false;
  }

  const onChange = (value: unknown) => {
    const propertyName = fieldName;
    const dataProxy = dataProxyService.current.dataProxy;

    if (dataProxy) {
      dataProxy[propertyName] = fieldValueConverter ? fieldValueConverter(value) : value;
    }
  };

  dynamicProps = {
    ...dynamicProps,
    onChange,
  };

  const tagCreator = useTestTag(caption || '');

  if (Component && dynamicProps) {
    const tooltipStyleByFieldType = clsx({
      'mb-2 ml-2 -translate-y-16 translate-x-16': ['DATEFIELD'].includes(fieldType),
    });
    const tooltipFinalStyle = clsx('bg-mono-1', tooltipStyleByFieldType);
    const fieldWrapperStyle = clsx('grow min-w-0', {
      'overflow-hidden': fieldType !== 'RICHTEXTAREA' && fieldType !== 'HISTORY',
      'overflow-visible': fieldType === 'RICHTEXTAREA' || fieldType === 'HISTORY',
    });

    return (
      <div className={isHidden ? 'hidden' : 'mb-3'}>
        <div className="relative flex w-full items-center" ref={tagCreator('dynamic-field')}>
          <div id={scrollId} data-skip-scroll-element={true} className={fieldWrapperStyle}>
            {hintMessage && hintMessage.length > 0 && !hintMessageAsPlaceholder ? (
              <Tooltip description={hintMessage} tooltipZIndexCheck={true} tooltipClassName={tooltipFinalStyle}>
                <Component {...dynamicProps} />
              </Tooltip>
            ) : (
              <Component {...dynamicProps} />
            )}
          </div>
          {helpMessage && <HelpComponent description={helpMessage} className="absolute right-0 top-0" />}
        </div>
      </div>
    );
  }

  return null;
};

export default DynamicField;
